import * as React from "react";
import { DatePicker } from "../../../../../../react-core/Datepicker/DatePicker";
import { SelectEditor } from "../../../../../../react-core/Html/SelectEditor";
import { TextBoxEditor } from "../../../../../../react-core/Html/TextBoxEditor";
import { addDays, addYears } from "../../../../../../react-core/utils/date";
import { JsonDateParser } from "../../../../../../react-core/Utils/JsonDateParser";
import { Frequency } from "../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { SubAccountType } from "../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { BatchType } from "../../../../../../Wrap/Constants/Enums/Orders/Batch/BatchType";
import { ContentDatePicker } from "../../../../../Shared/Entities/Constants/DateConstants";
import { ProcessType } from "../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../Shared/Process/Process/ProcessComponent";
import {
    batchTypeArrays,
    batchTypeArraysMap,
    datePickerOptions,
    OrderFrequencyArraysMap,
    OrderFrequencyTypeArrays,
} from "../Resource/Contents";

interface IOrderSetupProps extends IProcessComponentProps {
    process: ProcessType;
    type: SubAccountType;
}

interface IOrderSetupState {}

export class OrderSetup extends ProcessComponent<IOrderSetupProps, IOrderSetupState> {
    constructor(props: any) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        super.componentDidMount();
        if (this.props.process == ProcessType.Switch) {
            this.orderTypeOnChange(BatchType.OneOff);
        }
    }

    private renderOneOffOrder() {
        const { data, onChange, process } = this.props;
        return (
            <>
                <DatePicker
                    label="Action date"
                    formGroupCssWrapper="control-size-245"
                    selected={JsonDateParser.toDateNoOffset(data?.ActionDate)}
                    onChange={(v) =>
                        onChange({
                            ActionDate: JsonDateParser.toJsonDateNoOffset(v),
                            BatchType: v > new Date() ? BatchType.Standing : BatchType.OneOff,
                        })
                    }
                    atLeast={{
                        value: new Date(),
                        errorMessage: ContentDatePicker.CannotBeInThePast,
                    }}
                    required="Please select action date"
                    {...datePickerOptions}
                />
            </>
        );
    }

    private renderStandingOrder() {
        const { data, onChange } = this.props;
        const untilDate = addYears(JsonDateParser.toDate(data?.StartDate ? data?.StartDate : new Date()), 10);
        const startDate = new Date();
        return (
            <>
                <DatePicker
                    label="Start date"
                    formGroupCssWrapper="control-size-245"
                    selected={JsonDateParser.toDateNoOffset(data?.StartDate)}
                    onChange={(v) => onChange({ StartDate: JsonDateParser.toJsonDateNoOffset(v) })}
                    atLeast={{
                        value: startDate,
                        errorMessage: ContentDatePicker.CannotBeInThePast,
                    }}
                    required="Please select start date"
                    {...datePickerOptions}
                />

                <SelectEditor
                    options={OrderFrequencyTypeArrays}
                    getText={(v) => {
                        return OrderFrequencyArraysMap[v];
                    }}
                    cssWrapper="control-size-245"
                    required="Please select payment frequency"
                    selected={data?.Frequency}
                    label="Frequency"
                    onChange={(v) => onChange({ Frequency: v })}
                />

                <SelectEditor
                    options={[true, false]}
                    cssWrapper="control-size-245"
                    getText={(v) => {
                        return v ? "Until date" : "Until notified";
                    }}
                    required="Please select continue"
                    selected={data?.ContinueUntilDate}
                    label="Continue"
                    onChange={(v) => {
                        onChange({ ContinueUntilDate: v });
                    }}
                />

                {data?.ContinueUntilDate && (
                    <DatePicker
                        selected={JsonDateParser.toDateNoOffset(data?.UntilDate)}
                        formGroupCssWrapper="control-size-245"
                        label="Until date"
                        onChange={(v) =>
                            onChange({
                                UntilDate: JsonDateParser.toJsonDateNoOffset(v),
                            })
                        }
                        lessThan={{
                            errorMessage: "Until date must be within 10 years of the start date",
                            value: untilDate,
                        }}
                        atLeast={{
                            value: addDays(startDate, 1),
                            errorMessage: ContentDatePicker.CannotBeInThePast,
                        }}
                        alwaysValidateOnUpdate={false}
                        {...datePickerOptions}
                        required="Please select until date"
                    />
                )}
            </>
        );
    }

    private orderTypeOnChange(v: any) {
        const { onChange } = this.props;
        if (v == BatchType.OneOff)
            {onChange({
                BatchType: v,
                OrderType: v,
                ActionDate: JsonDateParser.toJsonDateNoOffset(new Date()),
                StartDate: null,
                Frequency: null,
                ContinueUntilDate: null,
                UntilDate: null,
            });}
        else
            {onChange({
                BatchType: v,
                OrderType: v,
                ActionDate: null,
                StartDate: JsonDateParser.toJsonDateNoOffset(new Date()),
                Frequency: Frequency.Monthly,
                ContinueUntilDate: false,
                UntilDate: null,
            });}
    }

    public render(): JSX.Element {
        const { data, process } = this.props;

        return (
            <>
                <h2 className="mb-18">
                    <span>Order setup</span>
                </h2>

                {process != ProcessType.Switch && (
                    <SelectEditor
                        label="Order type"
                        cssWrapper="control-size-245"
                        selected={data?.OrderType}
                        options={batchTypeArrays}
                        getText={(o) => batchTypeArraysMap[o]}
                        onChange={(v) => this.orderTypeOnChange(v)}
                    />
                )}

                {process == ProcessType.Switch && (
                    <TextBoxEditor
                        cssWrapper="control-size-430"
                        value={data?.OrderType == BatchType.OneOff ? "one-off" : data?.OrderType?.toString()}
                        label={"Order type"}
                        disabled={true}
                    />
                )}
                {data?.OrderType == BatchType.OneOff ? this.renderOneOffOrder() : this.renderStandingOrder()}
            </>
        );
    }
}
